import type { Dispatch, SetStateAction } from "react";
import { apiFetch } from "@/lib/api/client";
import type { GatewayAccount, NoticeType } from "@/types";

type Notify = (text: string, type?: NoticeType) => void;
export function useGatewayActions({ localApiOnline, gatewayAccountName, setGatewayAccountName, setGatewayAccounts, setGatewayLoginPending, setGatewayCheckingIds, notify }: {
  localApiOnline: boolean; gatewayAccountName: string; setGatewayAccountName: Dispatch<SetStateAction<string>>;
  setGatewayAccounts: Dispatch<SetStateAction<GatewayAccount[]>>; setGatewayLoginPending: Dispatch<SetStateAction<boolean>>;
  setGatewayCheckingIds: Dispatch<SetStateAction<Set<string>>>; notify: Notify;
}) {
  const connectGatewayAccount = async () => {
    if (!localApiOnline) return notify("Panel API недоступен");
    setGatewayLoginPending(true);
    try {
      const response = await apiFetch(`/api/gateway/accounts/login`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ name: gatewayAccountName.trim() || undefined, provider: "Codex" }) });
      const payload = await response.json(); if (!response.ok || !payload.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setGatewayAccounts((items) => [payload.account, ...items.filter((item) => item.id !== payload.account.id)]); setGatewayAccountName(""); notify("Код авторизации получен");
    } catch (error) { notify(`AI аккаунт не подключен: ${String(error instanceof Error ? error.message : error)}`); } finally { setGatewayLoginPending(false); }
  };
  const reconnectGatewayAccount = async (id: string) => {
    if (!localApiOnline) return notify("Panel API недоступен");
    setGatewayCheckingIds((items) => new Set(items).add(id));
    try {
      const response = await apiFetch(`/api/gateway/accounts/${encodeURIComponent(id)}/reconnect`, { method: "POST" }); const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setGatewayAccounts((items) => items.map((item) => item.id === id ? payload.account : item)); notify("Новый код авторизации получен");
    } catch (error) { notify(`AI аккаунт не переподключен: ${String(error instanceof Error ? error.message : error)}`); } finally {
      setGatewayCheckingIds((items) => { const next = new Set(items); next.delete(id); return next; });
    }
  };
  const refreshGatewayAccountStatus = async (id: string) => {
    setGatewayCheckingIds((items) => new Set(items).add(id));
    try {
      const response = await apiFetch(`/api/gateway/accounts/${encodeURIComponent(id)}/status`, { method: "POST" }); const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setGatewayAccounts((items) => items.map((item) => item.id === id ? payload.account : item)); notify("Статус AI аккаунта обновлен");
    } catch (error) { notify(`Статус не обновлен: ${String(error instanceof Error ? error.message : error)}`); } finally {
      setGatewayCheckingIds((items) => { const next = new Set(items); next.delete(id); return next; });
    }
  };
  const refreshAllGatewayAccountStatus = async (ids: string[]) => {
    const uniqueIds = [...new Set(ids)];
    if (!uniqueIds.length) return;
    await Promise.all(uniqueIds.map((id) => refreshGatewayAccountStatus(id)));
  };
  const downloadGatewayAccountAuth = async (id: string) => {
    if (!window.confirm("Файл содержит секрет авторизации Codex. Скачать его на этот компьютер?")) return;
    try {
      const response = await apiFetch(`/api/gateway/accounts/${encodeURIComponent(id)}/download`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `codex-auth-${id}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      notify("Файл авторизации скачан");
    } catch (error) {
      notify(`Файл авторизации не скачан: ${String(error instanceof Error ? error.message : error)}`);
    }
  };
  const deleteGatewayAccount = async (id: string) => {
    if (!window.confirm("Удалить AI подключение и локальные данные авторизации?")) return;
    try {
      const response = await apiFetch(`/api/gateway/accounts/${encodeURIComponent(id)}/delete`, { method: "POST" }); const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setGatewayAccounts((items) => items.filter((item) => item.id !== id)); notify("AI аккаунт удален");
    } catch (error) { notify(`AI аккаунт не удален: ${String(error instanceof Error ? error.message : error)}`); }
  };
  return { connectGatewayAccount, reconnectGatewayAccount, refreshGatewayAccountStatus, refreshAllGatewayAccountStatus, downloadGatewayAccountAuth, deleteGatewayAccount };
}
